// Boot, input → intent, title screen, and the window.__wg test API. (TECHNICAL_DESIGN.md §13.)
import { Application } from 'pixi.js';
import { BUILD, FEEDBACK_URL, MOBILE_MAX_EDGE } from './const';
import { isConfigured } from './firebase-config';
import type { AccountApi } from './types';
import { accrueBlessing, dayUTC } from './blessing';
import { Game, loadOrNull } from './game';
import { initAnalytics } from './analytics';
import { initTextures } from './textures';
import { mountUI } from './ui';
import { hasSave } from './save';
import { Sfx, mountSfx } from './sfx';
import { PALETTE } from './textures';

// The cloud account facade, set once the (optional) cloud layer loads. Undefined = no accounts.
let account: AccountApi | undefined;

async function boot(): Promise<void> {
  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  const uiRoot = document.getElementById('ui-root') as HTMLElement;

  const app = new Application();
  await app.init({
    canvas,
    background: 0x35431c, // muted meadow green: any sliver of uncovered edge blends with the world
    antialias: false,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  // Manage sizing explicitly — `resizeTo: window` mis-set the logical size on high-DPR displays
  // (renderer.width == window px, not × resolution), which broke camera centering. resize() sets
  // the logical screen AND the resolution-scaled buffer correctly.
  const fitRenderer = () => app.renderer.resize(window.innerWidth, window.innerHeight);
  fitRenderer(); // initial sizing at boot
  const fit = () => { fitRenderer(); game.onResize(); };
  window.addEventListener('resize', fit);
  window.addEventListener('orientationchange', fit);
  window.visualViewport?.addEventListener('resize', fit);

  initTextures(app);
  const game = new Game(app);
  initAnalytics(game); // error visibility always; GoatCounter only when configured on a real host
  const sfx = new Sfx();
  mountSfx(sfx, game);
  // Optional cloud accounts: only reachable when configured, so Rollup tree-shakes the whole
  // Firebase layer out of the bundle while `isConfigured` is false (the game stays zero-backend).
  if (isConfigured) {
    void import('./cloud').then(({ initCloud }) => {
      account = initCloud({
        getLocalSave: () => loadOrNull(),
        applySave: (sv) => { game.start(sv); mountUI(uiRoot, game, sfx, account); },
      });
      if (game.running) mountUI(uiRoot, game, sfx, account); // reflect the Account UI if already in-game
    });
  }
  // Browsers block audio until a user gesture — unlock on the first tap/click/key anywhere.
  const unlock = () => sfx.unlock();
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });

  // iOS ignores user-scalable=no, so touching the joystick can trigger pinch/double-tap zoom.
  // Suppress the browser's gesture handling over the game.
  (['gesturestart', 'gesturechange', 'gestureend'] as const).forEach((ev) =>
    document.addEventListener(ev, (e) => e.preventDefault() as unknown as void));
  document.addEventListener('dblclick', (e) => e.preventDefault());
  document.addEventListener('touchmove', (e) => { if (e.touches.length > 1) e.preventDefault(); }, { passive: false });

  // keyboard → intent
  const keys = new Set<string>();
  window.addEventListener('keydown', (e) => {
    const k = e.key.toLowerCase();
    keys.add(k);
    if (!game.running) return; // ignore gameplay keys on the title/creator (no save yet)
    if (k === 'e') game.tryInteract();
    if (e.key === ' ') { e.preventDefault(); game.tryAttack(); }
    if (k === '1') game.setWeapon('melee');
    if (k === '2') game.setWeapon('bow');
  });
  window.addEventListener('keyup', (e) => keys.delete(e.key.toLowerCase()));
  function readKeys(): { x: number; y: number } {
    let x = 0, y = 0;
    if (keys.has('a') || keys.has('arrowleft')) x -= 1;
    if (keys.has('d') || keys.has('arrowright')) x += 1;
    if (keys.has('w') || keys.has('arrowup')) y -= 1;
    if (keys.has('s') || keys.has('arrowdown')) y += 1;
    return { x, y };
  }

  // click on canvas → attack (desktop); E is the interact key
  canvas.addEventListener('pointerdown', () => { if (game.running) game.tryAttack(); });

  // main loop
  const coarse = window.matchMedia('(pointer: coarse)'); // cached — don't allocate a MQL per frame
  app.ticker.add((t) => {
    if (!game.running) return;
    try {
      // keyboard drives intent unless a joystick set it this frame (joystick writes directly)
      const k = readKeys();
      if (k.x !== 0 || k.y !== 0) game.intent.move = k;
      else if (!coarse.matches) game.intent.move = { x: 0, y: 0 };
      game.tick(t.deltaMS / 1000);
    } catch (err) {
      // one bad frame must never white-screen the game — log once and keep the loop alive
      console.error('Wildergrove tick error:', err);
    }
  });

  // save on hide (visibility + pagehide for iOS)
  const flush = () => { if (game.running) game.persist(); };
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
  window.addEventListener('pagehide', flush);

  // ---- title screen ----
  showTitle(uiRoot, game, sfx);

  // ---- test API ----
  installTestApi(game, uiRoot, sfx);
}

function showTitle(uiRoot: HTMLElement, game: Game, sfx: Sfx): void {
  uiRoot.innerHTML = '';
  const s = document.createElement('div');
  s.className = 'clickable';
  s.style.cssText = 'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:radial-gradient(circle at 50% 40%,#25341c,#0e130b);font-family:system-ui,sans-serif;color:#f2ead9';
  const title = document.createElement('h1');
  title.textContent = 'Wildergrove';
  title.style.cssText = 'font-size:44px;color:#cdb87a;letter-spacing:1px';
  const tag = document.createElement('p');
  tag.textContent = 'The grove is dimming. Something below drinks its light.';
  tag.style.cssText = 'opacity:.7;font-style:italic;font-size:14px;max-width:320px;text-align:center';
  s.append(title, tag);

  const mkBtn = (label: string) => {
    const b = document.createElement('button');
    b.className = 'clickable';
    b.textContent = label;
    b.style.cssText = 'padding:12px 28px;font-size:16px;border:none;border-radius:10px;background:#4a6a2a;color:#f2ead9;cursor:pointer;min-width:200px';
    return b;
  };

  if (hasSave()) {
    const sv = loadOrNull();
    const cont = mkBtn('Continue');
    cont.onclick = () => { sfx.unlock(); if (sv) { game.start(sv); mountUI(uiRoot, game, sfx, account); } };
    s.appendChild(cont);
    // gentle return trigger: if a daily blessing has banked and today's isn't claimed, say so
    if (sv) {
      const bl = { ...sv.blessing }; accrueBlessing(bl, Date.now());
      if (bl.banked > 0 && sv.blessing.claimedDayUTC !== dayUTC(Date.now())) {
        const hint = document.createElement('div');
        hint.textContent = '🌸 A blessing awaits in the grove';
        hint.style.cssText = 'font-size:13px;color:#cdb0f0;opacity:.9;margin-top:-4px';
        s.appendChild(hint);
      }
    }
  }
  const nw = mkBtn('New World');
  nw.onclick = () => { sfx.unlock(); showCreator(uiRoot, game, sfx); };
  s.appendChild(nw);

  // beta footer: version stamp + a feedback link testers can find without opening the menu
  const foot = document.createElement('div');
  foot.style.cssText = 'position:absolute;bottom:14px;left:0;right:0;display:flex;flex-direction:column;align-items:center;gap:4px;font-size:12px;opacity:.55';
  const ver = document.createElement('span');
  ver.textContent = BUILD;
  const fb = document.createElement('a');
  fb.className = 'clickable';
  fb.textContent = '💬 Send feedback';
  fb.href = `${FEEDBACK_URL}?title=${encodeURIComponent('[' + BUILD + '] ')}`;
  fb.target = '_blank'; fb.rel = 'noopener';
  fb.style.cssText = 'color:#cdb87a;text-decoration:underline;cursor:pointer';
  foot.append(ver, fb);
  s.appendChild(foot);

  uiRoot.appendChild(s);
}

/** Character creator: light palette customization + optional shared seed, then into the world. */
function showCreator(uiRoot: HTMLElement, game: Game, sfx: Sfx): void {
  uiRoot.innerHTML = '';
  const chosen = { body: 0, hair: 0, outfit: 0 };
  const wrap = document.createElement('div');
  wrap.className = 'clickable';
  wrap.style.cssText = 'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;background:radial-gradient(circle at 50% 40%,#25341c,#0e130b);font-family:system-ui,sans-serif;color:#f2ead9;padding:20px;overflow:auto';
  const h = document.createElement('h2');
  h.textContent = 'Shape your wanderer';
  h.style.cssText = 'color:#cdb87a;font-size:26px';
  wrap.appendChild(h);

  const preview = document.createElement('canvas');
  preview.width = 44; preview.height = 40;
  preview.style.cssText = 'width:88px;height:80px;image-rendering:pixelated;background:#3a5a2a55;border-radius:10px';
  const pctx = preview.getContext('2d')!;
  const drawPreview = () => {
    const skin = PALETTE.body[chosen.body], hair = PALETTE.hair[chosen.hair], outfit = PALETTE.outfit[chosen.outfit];
    const hex = (n: number) => `#${n.toString(16).padStart(6, '0')}`;
    pctx.clearRect(0, 0, 44, 40);
    pctx.fillStyle = 'rgba(0,0,0,0.28)'; pctx.beginPath(); pctx.ellipse(17, 33, 11, 4, 0, 0, 7); pctx.fill();
    pctx.fillStyle = hex(outfit); pctx.fillRect(9, 15, 16, 17);
    pctx.fillStyle = hex(skin); pctx.beginPath(); pctx.arc(17, 10, 7.5, 0, 7); pctx.fill();
    pctx.fillStyle = hex(hair); pctx.beginPath(); pctx.arc(17, 9.5, 7.9, Math.PI, Math.PI * 2); pctx.fill();
  };
  wrap.appendChild(preview);

  // name (shown on the Hero tab; optional — blank falls back to "Hero")
  const nameRow = document.createElement('div');
  nameRow.style.cssText = 'display:flex;align-items:center;gap:8px;margin-top:4px';
  const nameLab = document.createElement('span'); nameLab.textContent = 'Name'; nameLab.style.cssText = 'width:64px;font-size:13px;opacity:.8';
  const nameInput = document.createElement('input');
  nameInput.className = 'clickable'; nameInput.type = 'text'; nameInput.maxLength = 16; nameInput.placeholder = 'Wanderer';
  nameInput.style.cssText = 'padding:8px 10px;border-radius:8px;border:none;background:#0006;color:#f2ead9;width:150px;font-size:14px';
  nameRow.append(nameLab, nameInput);
  wrap.appendChild(nameRow);

  const rowFor = (label: string, key: 'body' | 'hair' | 'outfit', colors: number[]) => {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:8px';
    const lab = document.createElement('span'); lab.textContent = label; lab.style.cssText = 'width:64px;font-size:13px;opacity:.8';
    row.appendChild(lab);
    colors.forEach((c, i) => {
      const sw = document.createElement('button');
      sw.className = 'clickable wg-swatch';
      sw.dataset.key = key; sw.dataset.idx = String(i);
      sw.style.cssText = `width:34px;height:34px;border-radius:8px;cursor:pointer;background:#${c.toString(16).padStart(6, '0')};border:3px solid ${i === chosen[key] ? '#f2ead9' : 'transparent'}`;
      sw.onclick = () => {
        chosen[key] = i;
        row.querySelectorAll('button').forEach((b, j) => { (b as HTMLElement).style.borderColor = j === i ? '#f2ead9' : 'transparent'; });
        drawPreview(); sfx.play('ui');
      };
      row.appendChild(sw);
    });
    return row;
  };
  wrap.append(rowFor('Skin', 'body', PALETTE.body), rowFor('Hair', 'hair', PALETTE.hair), rowFor('Outfit', 'outfit', PALETTE.outfit));

  // optional shared seed (prefilled from ?seed= so a friend's link lands here)
  const seedRow = document.createElement('div');
  seedRow.style.cssText = 'display:flex;align-items:center;gap:8px;margin-top:4px';
  const seedLab = document.createElement('span'); seedLab.textContent = 'Seed'; seedLab.style.cssText = 'width:64px;font-size:13px;opacity:.8';
  const seedInput = document.createElement('input');
  seedInput.className = 'clickable'; seedInput.type = 'text'; seedInput.inputMode = 'numeric'; seedInput.placeholder = 'random';
  seedInput.style.cssText = 'padding:8px 10px;border-radius:8px;border:none;background:#0006;color:#f2ead9;width:150px;font-size:14px';
  const urlSeed = parseSeedParam();
  if (urlSeed != null) seedInput.value = String(urlSeed);
  seedRow.append(seedLab, seedInput);
  wrap.appendChild(seedRow);

  const go = document.createElement('button');
  go.className = 'clickable';
  go.textContent = 'Enter the grove';
  go.style.cssText = 'padding:12px 28px;font-size:16px;border:none;border-radius:10px;background:#4a6a2a;color:#f2ead9;cursor:pointer;min-width:200px;margin-top:6px';
  go.onclick = () => {
    sfx.unlock();
    const raw = seedInput.value.trim();
    const seed = raw ? (parseInt(raw, 10) >>> 0) : undefined;
    game.newWorld(Number.isFinite(seed as number) ? seed : undefined);
    game.setPalette(chosen);
    game.setName(nameInput.value);
    game.start(game.save);
    mountUI(uiRoot, game, sfx, account);
  };
  wrap.appendChild(go);

  drawPreview();
  uiRoot.appendChild(wrap);
}

/** Read + clamp a ?seed=NNNN query param to a uint32; null if absent/invalid. */
function parseSeedParam(): number | null {
  try {
    const v = new URLSearchParams(window.location.search).get('seed');
    if (v == null) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? (n >>> 0) : null;
  } catch { return null; }
}

function installTestApi(game: Game, uiRoot: HTMLElement, sfx: Sfx): void {
  (window as unknown as { __wg: unknown }).__wg = {
    newWorld: (seed?: number) => { game.newWorld(seed); if (!game.running) { game.start(game.save); mountUI(uiRoot, game, sfx, account); } },
    getState: () => game.snapshot(),
    give: (id: string, n: number) => game.addItem(id, n),
    eat: (id: string) => game.eat(id),
    interact: () => game.tryInteract(),
    attack: () => game.tryAttack(),
    setWeapon: (w: 'melee' | 'bow') => { game.weapon = w; },
    setMove: (x: number, y: number) => { game.intent.move = { x, y }; },
    spawnMob: (defId: string, dx = 60, dy = 0) => game.spawnMob(defId, game.save.player.x + dx, game.save.player.y + dy),
    getMobs: () => game.snapshot().mobs,
    teleport: (tx: number, ty: number) => game.teleport(tx, ty),
    setGold: (n: number) => game.setGold(n),
    setCombatSeed: (n: number) => game.setCombatSeed(n),
    setSpawnSeed: (n: number) => game.setSpawnSeed(n),
    setAmbientSpawns: (b: boolean) => { game.ambientSpawns = b; },
    setSafeZone: (b: boolean) => game.setSafeZone(b),
    craft: (id: string) => game.craft(id),
    build: (itemId: string) => game.buildFacing(itemId),
    nearCampfire: () => game.nearCampfire(),
    save: () => game.persist(),
    continueGame: () => { const sv = loadOrNull(); if (sv) { game.start(sv); mountUI(uiRoot, game, sfx, account); } },
    setMuted: (m: boolean) => sfx.setMuted(m),
    setPalette: (p: { body?: number; hair?: number; outfit?: number }) => game.setPalette(p),
    teleportHome: () => game.teleportHome(),
    cancelPortal: () => game.cancelPortal(),
    expandSanctuary: () => game.expandSanctuary(),
    enterDungeon: (floor?: number) => game.enterDungeon(floor),
    descend: () => game.descend(),
    exitDungeon: () => game.exitDungeon(),
    pickBlessing: (i: number) => game.pickBlessing(i),
    dungeonInfo: () => game.getDungeonInfo(),
    hurt: (n: number) => game.debugHurt(n),
    killNearest: () => game.debugKillNearest(),
    debugMobs: () => game.debugMobs(),
    debugCam: () => game.debugCam(),
    debugIsSolid: (x: number, y: number) => game.solidAtWorld(x, y),
    setLootSeed: (n: number) => game.setLootSeed(n),
    equip: (uid: string) => game.equip(uid),
    unequip: (slot: string) => game.unequip(slot as never),
    salvageBelow: (r: number) => game.salvageBelow(r as any),
    giveGear: (partial: any) => game.debugGiveGear(partial),
    giveRelic: (partial: any) => game.debugGiveRelic(partial),
    equipRelic: (uid: string, slot: number) => game.equipRelic(uid, slot),
    unequipRelic: (slot: number) => game.unequipRelic(slot),
    salvageRelic: (uid: string) => game.salvageRelic(uid),
    grantAll: (portals?: number) => game.debugGrant(portals),
    empower: (uid: string) => game.empower(uid),
    setTier: (n: number) => game.setTier(n),
    claimBlessing: () => game.claimBlessing(),
    tickSeconds: (sec: number) => { const steps = Math.round(sec * 60); for (let i = 0; i < steps; i++) game.step(1 / 60); },
    skipToNight: () => game.skipToNight(),
    skipDays: (n: number) => game.skipDays(n),
    pacing: () => game.pacing(),
    exportSave: () => game.exportSave(),
    importSave: (code: string) => game.importSave(code),
    perf: () => game.perf(),
    isRunning: () => game.running,
  };
}

// PWA service worker — PRODUCTION ONLY. On the dev server it would cache Vite's output and serve
// stale builds (changes never appear), so on localhost we actively unregister any leftover SW and
// wipe its caches instead of registering one.
if ('serviceWorker' in navigator) {
  const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV
    || ['localhost', '127.0.0.1'].includes(location.hostname);
  if (isDev) {
    navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.unregister())).catch(() => {});
    if (typeof caches !== 'undefined') caches.keys().then((ks) => ks.forEach((k) => caches.delete(k))).catch(() => {});
  } else {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('./sw.js').catch(() => { /* offline is a bonus */ });
    });
  }
}

boot().catch((err) => {
  console.error('Wildergrove boot failed:', err);
  const r = document.getElementById('ui-root');
  if (r) r.innerHTML = `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#f88;font-family:sans-serif">Boot error — see console</div>`;
});
